// Watchlist Content - Used inside Tracking Module
// This is the content portion without Navbar wrapper

import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../../config/api";
import {
  Container,
  Package,
  MapPin,
  Mail,
  Tag,
  CheckCircle,
  Eye,
  Trash2,
  Edit2,
  X,
  Save,
  RefreshCw,
  Target,
  Calendar,
  FolderOpen,
  ExternalLink,
} from "lucide-react";


const StatusBadge = ({ status }) => {
  const styles = {
    watching: "bg-blue-100 text-blue-700 border-blue-200",
    arrived: "bg-green-100 text-green-700 border-green-200",
    completed: "bg-gray-100 text-gray-500 border-gray-200",
  };

  const icons = {
    watching: <Eye className="h-3 w-3" />,
    arrived: <CheckCircle className="h-3 w-3" />,
    completed: <CheckCircle className="h-3 w-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.watching}`}>
      {icons[status]}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const ContainerCard = ({ container, onUpdate, onDelete, onComplete, isEuropacificUser, onNavigateToPF }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    notification_emails: container.notification_emails?.join(", ") || "",
    europacific_ref: container.europacific_ref || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const emails = editData.notification_emails.split(/[,;\s]+/).filter(e => e.trim());
      await onUpdate(container.container_number, {
        notification_emails: emails,
        europacific_ref: editData.europacific_ref || null,
      });
      setIsEditing(false);
      toast.success("Container updated");
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="card-surface p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Container className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-code font-bold text-foreground text-lg">{container.container_number}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {container.europacific_ref && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Tag className="h-3 w-3" />{container.europacific_ref}
                </span>
              )}
              {container.pf_number && (
                <button 
                  onClick={() => onNavigateToPF && onNavigateToPF(container.pf_id)}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
                >
                  <FolderOpen className="h-3 w-3" />{container.pf_number}
                  <ExternalLink className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          </div>
        </div>
        <StatusBadge status={container.status} />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Target className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Destination:</span>
          <span className="font-medium text-foreground">{container.destination_port}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Added:</span>
          <span className="font-medium text-foreground">{new Date(container.added_at || container.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {container.arrival_date && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Arrived at destination on {new Date(container.arrival_date).toLocaleDateString()}
          </p>
        </div>
      )}

      {isEditing ? (
        <div className="space-y-3 border-t border-border pt-4 mt-4">
          {isEuropacificUser && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Europacific Reference</label>
              <Input
                value={editData.europacific_ref}
                onChange={(e) => setEditData(prev => ({ ...prev, europacific_ref: e.target.value }))}
                placeholder="EP-2026-XXXXX"
                className="input-europacific"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Notification Emails</label>
            <Input
              value={editData.notification_emails}
              onChange={(e) => setEditData(prev => ({ ...prev, notification_emails: e.target.value }))}
              placeholder="email1@example.com, email2@example.com"
              className="input-europacific"
              disabled={!isEuropacificUser}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={loading} className="btn-primary">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 mr-1" />Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          {container.notification_emails?.length > 0 && (
            <div className="border-t border-border pt-4 mt-4">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Mail className="h-3 w-3" />Notifications to:
              </p>
              <div className="flex flex-wrap gap-1">
                {container.notification_emails.map((email, i) => (
                  <span key={i} className="text-xs bg-secondary px-2 py-1 rounded">{email}</span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-4 pt-4 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Edit2 className="h-4 w-4 mr-1" />Edit
            </Button>
            {container.status !== "completed" && (
              <Button size="sm" variant="outline" onClick={() => onComplete(container.container_number)} className="text-green-600 hover:text-green-700">
                <CheckCircle className="h-4 w-4 mr-1" />Complete
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onDelete(container.container_number)} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4 mr-1" />Remove
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

const HBLCard = ({ hbl, onUpdateEmails, isEuropacificUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    notification_emails: hbl.notification_emails?.join(", ") || "",
    europacific_ref: hbl.europacific_ref || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const emails = editData.notification_emails.split(/[,;\s]+/).filter(e => e.trim());
      await onUpdateEmails(hbl.hbl_number, emails, editData.europacific_ref);
      setIsEditing(false);
      toast.success("HBL updated");
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="card-surface p-5 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-accent" />
          </div>
          <div>
            <h3 className="font-code font-bold text-foreground text-lg">{hbl.hbl_number}</h3>
            {hbl.europacific_ref && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Tag className="h-3 w-3" />{hbl.europacific_ref}
              </p>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${
          hbl.source === "freightlinks" ? "bg-purple-100 text-purple-700 border-purple-200" : "bg-gray-100 text-gray-600 border-gray-200"
        }`}>
          {hbl.source === "freightlinks" ? "Freightlinks" : "Manual"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        {hbl.container_number && (
          <div className="flex items-center gap-2">
            <Container className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Container:</span>
            <span className="font-code font-medium text-foreground">{hbl.container_number}</span>
          </div>
        )}
        {hbl.destination && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Dest:</span>
            <span className="font-medium text-foreground">{hbl.destination}</span>
          </div>
        )}
        {hbl.eta && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">ETA:</span>
            <span className="font-medium text-foreground">{hbl.eta}</span>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-3 border-t border-border pt-4 mt-4">
          {isEuropacificUser && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Europacific Reference</label>
              <Input
                value={editData.europacific_ref}
                onChange={(e) => setEditData(prev => ({ ...prev, europacific_ref: e.target.value }))}
                placeholder="EP-LCL-2026-XXXXX"
                className="input-europacific"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Notification Emails</label>
            <Input
              value={editData.notification_emails}
              onChange={(e) => setEditData(prev => ({ ...prev, notification_emails: e.target.value }))}
              placeholder="email1@example.com, email2@example.com"
              className="input-europacific"
              disabled={!isEuropacificUser}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={loading} className="btn-primary">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 mr-1" />Cancel
            </Button>
          </div>
        </div>
      ) : (
        <>
          {hbl.notification_emails?.length > 0 && (
            <div className="border-t border-border pt-4 mt-4">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Mail className="h-3 w-3" />Notifications to:
              </p>
              <div className="flex flex-wrap gap-1">
                {hbl.notification_emails.map((email, i) => (
                  <span key={i} className="text-xs bg-secondary px-2 py-1 rounded">{email}</span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-4 pt-4 border-t border-border">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
              <Edit2 className="h-4 w-4 mr-1" />Edit Notifications
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default function WatchlistContent() {
  const [activeTab, setActiveTab] = useState("containers");
  const [containers, setContainers] = useState([]);
  const [hbls, setHbls] = useState([]);
  const [summary, setSummary] = useState({ watching: 0, arrived: 0, completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("mine"); // "mine" or "all"
  const { getAuthHeaders, isEuropacificUser } = useAuth();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      // Add owner filter query param for containers
      const ownerParam = isEuropacificUser ? `?owner=${ownerFilter}` : "";
      const [containersRes, summaryRes, hblsRes] = await Promise.all([
        axios.get(`${API_URL}/tracking/watched${ownerParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/tracking/watched/summary${ownerParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/lcl/manual-hbls${ownerParam}`, { headers: getAuthHeaders() }),
      ]);
      setContainers(containersRes.data);
      setSummary(summaryRes.data);
      setHbls(hblsRes.data);
    } catch (error) {
      toast.error("Failed to load watchlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [ownerFilter]); // Re-fetch when owner filter changes

  const handleUpdateContainer = async (containerNumber, data) => {
    await axios.put(`${API_URL}/tracking/watch/${containerNumber}`, data, { headers: getAuthHeaders() });
    fetchData();
  };

  const handleCompleteContainer = async (containerNumber) => {
    await axios.put(`${API_URL}/tracking/watch/${containerNumber}/complete`, {}, { headers: getAuthHeaders() });
    toast.success("Container marked as completed");
    fetchData();
  };

  const handleDeleteContainer = async (containerNumber) => {
    if (!window.confirm(`Remove ${containerNumber} from your watchlist?`)) return;
    await axios.delete(`${API_URL}/tracking/watch/${containerNumber}`, { headers: getAuthHeaders() });
    toast.success("Container removed from watchlist");
    fetchData();
  };

  const handleUpdateHBLEmails = async (hblNumber, emails, ref) => {
    await axios.put(`${API_URL}/lcl/hbl/${hblNumber}/notifications`, { notification_emails: emails, europacific_ref: ref }, { headers: getAuthHeaders() });
    fetchData();
  };

  const handleNavigateToPF = (pfId) => {
    if (pfId) {
      navigate("/position-folders");
      // Store the PF ID to open in query param for now
      toast.info("Navigate to Position Folders to view details");
    }
  };

  const handleRefreshAllFromSeaRates = async () => {
    if (!isEuropacificUser) {
      toast.error("Staff access required");
      return;
    }
    
    const trackingCount = containers.filter(c => c.status === "watching" || c.status === "tracking").length;
    if (trackingCount === 0) {
      toast.info("No containers to refresh");
      return;
    }
    
    if (!window.confirm(`This will refresh ${trackingCount} container(s) from SeaRates and update their Position Folders. Continue?`)) {
      return;
    }
    
    setRefreshingAll(true);
    setRefreshProgress({ current: 0, total: trackingCount, status: "Starting..." });
    
    try {
      const response = await axios.post(
        `${API_URL}/tracking/refresh-all-watchlist`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const { updated, failed, results } = response.data;
      
      setRefreshProgress(null);
      
      if (updated > 0) {
        toast.success(`✓ Updated ${updated} container(s) from SeaRates`);
      }
      if (failed > 0) {
        toast.warning(`${failed} container(s) failed to update`);
      }
      
      // Show details
      if (results && results.length > 0) {
        const successItems = results.filter(r => r.success);
        if (successItems.length > 0) {
          const pfCount = successItems.reduce((sum, r) => sum + (r.pfs_updated || 0), 0);
          if (pfCount > 0) {
            toast.info(`Updated milestones in ${pfCount} Position Folder(s)`);
          }
        }
      }
      
      // Refresh the list
      fetchData();
      
    } catch (error) {
      console.error("Refresh all error:", error);
      toast.error(error.response?.data?.detail || "Failed to refresh containers");
      setRefreshProgress(null);
    } finally {
      setRefreshingAll(false);
    }
  };

  const filteredContainers = statusFilter === "all" ? containers : containers.filter(c => c.status === statusFilter);

  return (
    <div className="max-w-[1800px] mx-auto p-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Eye className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.watching}</p>
              <p className="text-xs text-muted-foreground">Watching</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.arrived}</p>
              <p className="text-xs text-muted-foreground">Arrived</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{summary.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{hbls.length}</p>
              <p className="text-xs text-muted-foreground">HBLs</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <Button
            variant={activeTab === "containers" ? "default" : "outline"}
            onClick={() => setActiveTab("containers")}
            className={activeTab === "containers" ? "btn-primary" : ""}
          >
            <Container className="h-4 w-4 mr-2" />
            Containers ({containers.length})
          </Button>
          <Button
            variant={activeTab === "hbls" ? "default" : "outline"}
            onClick={() => setActiveTab("hbls")}
            className={activeTab === "hbls" ? "btn-primary" : ""}
          >
            <Package className="h-4 w-4 mr-2" />
            HBLs ({hbls.length})
          </Button>
        </div>

        <div className="flex items-center gap-3">
          {/* Mine/All Toggle for Europacific Staff */}
          {isEuropacificUser && (
            <div className="flex items-center bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setOwnerFilter("mine")}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  ownerFilter === "mine" 
                    ? "bg-white text-slate-900 shadow-sm" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="watchlist-filter-mine"
              >
                Mine
              </button>
              <button
                onClick={() => setOwnerFilter("all")}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  ownerFilter === "all" 
                    ? "bg-white text-slate-900 shadow-sm" 
                    : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid="watchlist-filter-all"
              >
                All
              </button>
            </div>
          )}
          
          {activeTab === "containers" && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-border rounded-lg text-sm bg-white"
            >
              <option value="all">All Status</option>
              <option value="watching">Watching</option>
              <option value="arrived">Arrived</option>
              <option value="completed">Completed</option>
            </select>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          
          {/* Refresh All from SeaRates Button - Staff Only */}
          {isEuropacificUser && activeTab === "containers" && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleRefreshAllFromSeaRates} 
              disabled={refreshingAll || loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="refresh-all-searates-btn"
            >
              {refreshingAll ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Target className="h-4 w-4 mr-1" />
                  Update All from SeaRates
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your watchlist...</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {activeTab === "containers" ? (
            <motion.div key="containers" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {filteredContainers.length === 0 ? (
                <div className="card-surface p-12 text-center">
                  <Container className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No containers {statusFilter !== "all" && `with status "${statusFilter}"`}
                  </h3>
                  <p className="text-muted-foreground">Start tracking containers from the FCL Tracking tab</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredContainers.map((container) => (
                    <ContainerCard
                      key={container.id}
                      container={container}
                      onUpdate={handleUpdateContainer}
                      onDelete={handleDeleteContainer}
                      onComplete={handleCompleteContainer}
                      isEuropacificUser={isEuropacificUser}
                      onNavigateToPF={handleNavigateToPF}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="hbls" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {hbls.length === 0 ? (
                <div className="card-surface p-12 text-center">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No HBLs tracked</h3>
                  <p className="text-muted-foreground">Track HBLs from the LCL / BL tab</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {hbls.map((hbl) => (
                    <HBLCard
                      key={hbl.id}
                      hbl={hbl}
                      onUpdateEmails={handleUpdateHBLEmails}
                      isEuropacificUser={isEuropacificUser}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
